import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Box, Container, Divider, Grid, Typography } from '@mui/material';
import styles from './reviewIncident.module.scss';
import { IncidentProps } from '../viewServices/createIncident.view.slice';
import { dateTime } from '../../../time/datetime';
import {
  createLocalisation,
  LocalisationProps,
} from '../../../translation/helpers/enumLocalisation';
import {
  MechanismOfInjury,
  PlayType,
  SessionType,
  SportType,
} from '../smart/completeIncident.enum';
import { useCustomTranslation } from '../../../translation/customTranslation';
import { currentLocaleSports } from '../../../locale/LocaleConfig';

interface ReviewIncidentProps {
  incidentData?: IncidentProps;
  playerName?: string;
}
const sessionTypeList: LocalisationProps[] = Object.values(
  createLocalisation(SessionType, 'incident.sessionType'),
);
const playTypeList: LocalisationProps[] = Object.values(
  createLocalisation(PlayType, 'incident.playType'),
);
const headImpactTypeList: LocalisationProps[] = Object.values(
  createLocalisation(MechanismOfInjury, 'incident.mechanismOfInjury'),
);

export function ReviewIncidentDumb({
  incidentData,
  playerName,
}: ReviewIncidentProps) {
  const { t } = useTranslation();
  const { t: tCustom } = useCustomTranslation();

  return (
    <Container sx={{ borderRadius: 4 }} maxWidth="md">
      <Typography variant="subtitle1" sx={{ p: 2 }}>
        {t('incident.REVIEW_INCIDENT_HEADER')}
      </Typography>
      <Divider />
      <Grid sx={{ p: 2 }}>
        <Grid container justifyContent="space-between">
          <Grid item sx={{ my: 1 }}>
            {t('incident.REVIEW_INCIDENT_LABEL_PLAYER')}
          </Grid>
          <Grid item sx={{ my: 1 }}>
            <Typography variant="body1" fontWeight="600">
              {playerName}
            </Typography>
          </Grid>
        </Grid>
        <Grid container justifyContent="space-between">
          <Grid item sx={{ my: 1 }}>
            {t('incident.REVIEW_INCIDENT_LABEL_DATE_TIME')}
          </Grid>
          <Grid item sx={{ my: 1 }}>
            <Typography variant="body1" fontWeight="600">
              {dateTime.format(
                incidentData?.incidentDateTime,
                t('common.datetime.FORMAT_STANDARD_DATE_TIME'),
              )}
            </Typography>
          </Grid>
        </Grid>
        <Grid container justifyContent="space-between">
          <Grid item sx={{ my: 1 }}>
            {t('incident.REVIEW_INCIDENT_LABEL_RED_FLAGS')}
          </Grid>
          <Grid item sx={{ my: 1 }}>
            <Typography
              variant="body1"
              fontWeight="600"
              data-testid="redFlagsValue"
            >
              {incidentData?.redFlags
                ? t('incident.REVIEW_INCIDENT_RED_FLAGS_YES')
                : t('incident.REVIEW_INCIDENT_RED_FLAGS_NO')}
            </Typography>
          </Grid>
        </Grid>
        {incidentData?.activity && (
          <Grid container justifyContent="space-between">
            <Grid item sx={{ my: 1 }}>
              {t('incident.sport.ACTIVITY_TITLE')}
            </Grid>
            <Grid item sx={{ my: 1 }}>
              <Typography variant="body1" fontWeight="600">
                {incidentData?.activity &&
                incidentData?.activity === SportType.OTHER
                  ? incidentData?.activityOther && incidentData?.activityOther
                  : tCustom(
                      currentLocaleSports.find(
                        (act) => act.value === incidentData?.activity,
                      )?.i18nKey || '',
                    )}
              </Typography>
            </Grid>
          </Grid>
        )}
        {incidentData?.sessionType && (
          <Grid container justifyContent="space-between">
            <Grid item sx={{ my: 1 }}>
              {t('incident.sessionType.SESSION_TYPE_TITLE')}
            </Grid>
            <Grid item sx={{ my: 1 }}>
              <Typography variant="body1" fontWeight="600">
                {incidentData?.sessionType &&
                incidentData?.sessionType === SessionType.OTHER
                  ? incidentData?.sessionTypeOther &&
                    incidentData?.sessionTypeOther
                  : tCustom(
                      sessionTypeList.find(
                        (act) => act.value === incidentData?.sessionType,
                      )?.i18nKey || '',
                    )}
              </Typography>
            </Grid>
          </Grid>
        )}
        {incidentData?.playType && (
          <Grid container justifyContent="space-between">
            <Grid item sx={{ my: 1 }}>
              {t('incident.playType.PLAY_TYPE_TITLE')}
            </Grid>
            <Grid item sx={{ my: 1 }}>
              <Typography variant="body1" fontWeight="600">
                {incidentData?.playType &&
                incidentData?.playType === PlayType.OTHER
                  ? incidentData?.playTypeOther && incidentData?.playTypeOther
                  : tCustom(
                      playTypeList.find(
                        (act) => act.value === incidentData?.playType,
                      )?.i18nKey || '',
                    )}
              </Typography>
            </Grid>
          </Grid>
        )}
        {incidentData?.headImpactType && (
          <Grid container justifyContent="space-between">
            <Grid item sx={{ my: 1 }}>
              {t('incident.mechanismOfInjury.HEAD_IMPACT_TYPE_TITLE')}
            </Grid>
            <Grid item sx={{ my: 1 }}>
              <Typography variant="body1" fontWeight="600">
                {incidentData?.headImpactType &&
                incidentData?.headImpactType === MechanismOfInjury.OTHER
                  ? incidentData?.headImpactTypeOther &&
                    incidentData?.headImpactTypeOther
                  : tCustom(
                      headImpactTypeList.find(
                        (act) => act.value === incidentData?.headImpactType,
                      )?.i18nKey || '',
                    )}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Container>
  );
}
